import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const content = {
    en: {
      title: "Get In Touch",
      subtitle: "Ready to start your English learning journey? Contact us today!",
      name: "Full Name",
      email: "Email Address",
      phone: "Phone Number",
      message: "Your Message",
      submit: "Send Message",
      contactInfo: "Contact Information",
      address: "100 Consilium Place, Toronto, Ontario, M1T 3N1, Canada",
      viewOnMap: "View on Google Maps",
      successMessage: "Message sent successfully! We'll get back to you soon.",
      errorMessage: "Please fill in all fields"
    },
    ar: {
      title: "تواصل معنا",
      subtitle: "هل أنت مستعد لبدء رحلة تعلم اللغة الإنجليزية؟ اتصل بنا اليوم!",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
      message: "رسالتك",
      submit: "إرسال الرسالة",
      contactInfo: "معلومات الاتصال",
      address: "100 Consilium Place، تورونتو، أونتاريو، M1T 3N1، كندا",
      viewOnMap: "عرض على خرائط جوجل",
      successMessage: "تم إرسال الرسالة بنجاح! سنعود إليك قريباً.",
      errorMessage: "يرجى ملء جميع الحقول"
    }
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: t.errorMessage,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Submit to backend
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
        language: language
      });
      
      toast({
        title: t.successMessage,
        className: "bg-teal-600 text-white"
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: language === 'en' ? 'Error sending message. Please try again.' : 'خطأ في إرسال الرسالة. حاول مرة أخرى.',
        variant: "destructive"
      });
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-slate-50 to-teal-50/30">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{t.title}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.name}
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.name}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.email}
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.email}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.phone}
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.phone}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.message}
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.message}
                  rows={5}
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6 rounded-xl transition-colors"
              >
                {isSubmitting ? (
                  <Loader2 className={`w-5 h-5 animate-spin ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                ) : (
                  <Send className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                )}
                {isSubmitting ? (language === 'en' ? 'Sending...' : 'جاري الإرسال...') : t.submit}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.contactInfo}</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">
                      {language === 'en' ? 'Phone' : 'الهاتف'}
                    </p>
                    <a href="tel:+16479745620" className="text-gray-600 hover:text-teal-600 transition-colors">
                      +1 647 974 5620
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">
                      {language === 'en' ? 'Email' : 'البريد الإلكتروني'}
                    </p>
                    <a href="mailto:info@canadaenglishlife.com" className="text-gray-600 hover:text-teal-600 transition-colors">
                      info@canadaenglishlife.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">
                      {language === 'en' ? 'Address' : 'العنوان'}
                    </p>
                    <p className="text-gray-600 mb-2">
                      {t.address}
                    </p>
                    <p className="text-sm font-semibold text-teal-600 mb-1">
                      {language === 'en' ? 'Countries We Serve:' : 'الدول التي نخدمها:'}
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {language === 'en' 
                        ? 'Saudi Arabia 🇸🇦 • UAE 🇦🇪 • Kuwait 🇰🇼 • Qatar 🇶🇦 • Bahrain 🇧🇭 • Oman 🇴🇲 • Jordan 🇯🇴 • Egypt 🇪🇬 • Morocco 🇲🇦 • Tunisia 🇹🇳 • Algeria 🇩🇿 • All Arab Countries & Africa'
                        : 'السعودية 🇸🇦 • الإمارات 🇦🇪 • الكويت 🇰🇼 • قطر 🇶🇦 • البحرين 🇧🇭 • عمان 🇴🇲 • الأردن 🇯🇴 • مصر 🇪🇬 • المغرب 🇲🇦 • تونس 🇹🇳 • الجزائر 🇩🇿 • جميع الدول العربية وأفريقيا'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map placeholder or additional info */}
            <a
              href="https://www.google.com/maps/search/?api=1&query=100+Consilium+Place+Toronto+Ontario+M1T+3N1+Canada"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl shadow-xl overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-300"
            >
              <div className="aspect-video bg-gradient-to-br from-teal-100 to-emerald-100 flex flex-col items-center justify-center group-hover:from-teal-200 group-hover:to-emerald-200 transition-all duration-300">
                <MapPin className="w-16 h-16 text-teal-600 mb-3 group-hover:scale-110 transition-transform" />
                <p className="text-teal-700 font-semibold text-lg group-hover:text-teal-800">
                  {t.viewOnMap}
                </p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
